import { useState, useRef, useCallback, useEffect } from 'react';
import debounce from 'lodash/debounce';

/**
 * Defines an effect that appears when a new column is added, and does two things in sequence:
 *  - Scrolls to reveal the new column
 *  - Indicates the new column
 *
 * The hook handles the first part, but leaves the second part for the consuming component to
 * handle. However, it returns a state variable (isIndicatingNewColumn) to communicate when it's
 * time to indicate the column, as well as a callback (onFinishedIndicatingNewColumn) that the
 * consuming component should call when the indication is done.
 */
export function useNewColumnIndication(initialColumnCount: number) {
  const [isIndicatingNewColumn, setIsIndicatingNewColumn] = useState(false);
  const [columnCount, setColumnCount] = useState(initialColumnCount);
  const [previousColumnCount, setPreviousColumnCount] = useState(columnCount);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const isScrollingToNewColumn = useRef<boolean>(false);

  // We use ref instead of state here because state will not be updated
  // inside event listeners, as it belongs to the initial render only.
  const setIsScrollingToNewColumn = (value: boolean) => {
    isScrollingToNewColumn.current = value;
  };

  // Listen to scroll in order to know when it's time to indicate the new column
  const onScroll = useCallback(
    debounce(() => {
      if (isScrollingToNewColumn.current) {
        setIsIndicatingNewColumn(true);
        setIsScrollingToNewColumn(false);
      }
    }, 300),
    []
  );

  const scrollToEnd = useCallback(() => {
    if (!scrollableRef.current) {
      return;
    }

    const newPosition =
      scrollableRef.current.scrollWidth - scrollableRef.current.clientWidth;

    scrollableRef.current.scrollTo({
      left: newPosition,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    if (columnCount > previousColumnCount) {
      setIsScrollingToNewColumn(true);
      scrollToEnd();
    }
    setPreviousColumnCount(columnCount);
  }, [columnCount, previousColumnCount, scrollToEnd]);

  useEffect(() => {
    const current = scrollableRef.current;

    if (current) {
      current.addEventListener('scroll', onScroll);
      return () => current.removeEventListener('scroll', onScroll);
    }

    return () => {};
  }, [onScroll]);

  const onFinishedIndicatingNewColumn = useCallback(
    () => setIsIndicatingNewColumn(false),
    []
  );

  return {
    /** Lets the consuming component know when to indicate the new column */
    isIndicatingNewColumn,
    /** Lets the consuming component communicate back when the indication is done */
    onFinishedIndicatingNewColumn,
    /** The consuming component should call this when the column count changes */
    setColumnCount,
    /** This ref should be set on the scrollable container of the columns */
    scrollableRef,
  };
}
